import { SAVED_GAME_RETIRED_MESSAGE, type TableStatusResponse } from "@bing/shared";
import { fa } from "../i18n/fa";
import { isChannelRefusal } from "./channel-refusal";

/**
 * Colyseus matchmake error for a failed `joinById` — the room id is no longer
 * joinable. The server raises it for BOTH failure modes, telling them apart
 * only by message (`MatchMaker.ts`): `room "X" not found` (disposed/finished)
 * vs `room "X" is locked` (full, or the game already started).
 */
const MATCHMAKE_INVALID_ROOM_ID = 4212;

/** Whether this is a failed join-by-room-id at all (vs a network/system error). */
export function isJoinFailure(error: unknown): boolean {
  return roomFailure(error) !== null;
}

type RoomFailure = "gone" | "locked";

function roomFailure(error: unknown): RoomFailure | null {
  // THE GAME-SERVER'S CHANNEL REFUSAL FIRST: its extended form carries admin-written channel titles,
  // and a title reading «is locked» / «not found» must not be taken for a room's fate (or open the
  // ticket fallback). Recognised by its exact first line — `channel-refusal.ts`.
  if (isChannelRefusal(error)) return null;
  const message = typeof error === "string" ? error : error instanceof Error ? error.message : "";
  if (/is locked/i.test(message)) return "locked";
  // LOSING THE RACE FOR THE LAST SEAT. Colyseus refuses a join at `maxClients` two ways: the
  // room's `locked` flag (above) once that has reached the driver listing, and — in the window
  // before it does — `SeatReservationError: "<id> is already full."` from `_reserveSeat`. Same
  // event, and to the player the same sentence («میز پر است»), but this one used to match
  // neither pattern and fall through to the generic connection error. Public-table invites make
  // it far likelier: the seat is contested from two directions at once (a quick-matcher and the
  // invited friend), which is exactly the race this feature accepts by not reserving a seat.
  if (/already full/i.test(message)) return "locked";
  // OUR OWN refusal, and it is English — `online-controller.ts` throws `new Error("Table is full")`
  // when every seat is taken. It matches neither pattern above («already full» is Colyseus's
  // wording, not ours), so it fell through to the generic connection error: 32 events / 30 players
  // in 24 h on prod told their connection had failed by a table that was simply full. The room is
  // ALIVE, so this is `locked` — which also keeps `isRoomLocked` true and the re-join ticket worth
  // trying. ⏭ The server string itself should be Persian; that needs a game-server recreate (every
  // live table dropped), so it is handled here for free and left owed there.
  if (/table is full/i.test(message)) return "locked";
  if (/not found/i.test(message)) return "gone";
  // COLYSEUS SAYS «GONE» TWO WAYS, AND WE ONLY KNEW ONE. `MatchMaker` answers a join for a room
  // that has been reaped with `room "<id>" has been disposed.` — no «not found» in it — and that
  // was the SINGLE BIGGEST unclassified cause on prod: 238 events / ~186 players in 24 h, every
  // one of them shown «خطا در اتصال به سرور بازی» for a table that had simply ended. A disposed
  // room is `gone`: no ticket revives it, so `isRoomLocked` must stay false here.
  if (/has been disposed/i.test(message)) return "gone";
  const code =
    typeof error === "object" && error !== null && "code" in error
      ? (error as { code?: unknown }).code
      : undefined;
  return code === MATCHMAKE_INVALID_ROOM_ID ? "gone" : null;
}

/**
 * IS THIS THE ONE REFUSAL A RE-JOIN TICKET CAN OPEN?
 *
 * A LOCKED room is alive and shut — full, or the game started — and `client.reconnect(token)`
 * walks past that lock by design. A room that is «not found» is disposed and no ticket revives it,
 * so trying costs a round-trip and changes nothing. Exported so the connector's fallback and the
 * message layer answer the question the same way, from one implementation.
 */
export function isRoomLocked(error: unknown): boolean {
  return roomFailure(error) === "locked";
}

/**
 * The clean Persian reason for a failed join-by-room-id (an accepted game
 * invite or a `t_<game>_<roomId>` deep link), or `null` when the error is not a
 * recognised join failure (callers keep their generic message). Accepts the raw
 * rejection (`MatchMakeError` with `.code`), a plain `Error`, or the bare
 * message string the room's `onError` handler delivers.
 *
 * This is the answer we can give WITHOUT asking anyone — deliberately vague, because
 * Colyseus's two reasons genuinely do not say what happened. {@link tableJoinReason}
 * gives the true one.
 */
export function joinErrorFa(error: unknown): string | null {
  const failure = roomFailure(error);
  if (failure === "locked") return fa.invite.roomLocked;
  if (failure === "gone") return fa.invite.roomGone;
  return null;
}

/**
 * THE TRUE REASON an invite link no longer works, using what the table itself recorded.
 *
 * The bug: tap an invite to a game that has ENDED and the app said «میز پر است یا بازی
 * شروع شده است» — the table is not full, and "the game started" is a strange way to say
 * it finished twenty minutes ago. Colyseus cannot tell us more (a finished table whose
 * winner is still reading the summary is simply «locked»), so the game-server writes
 * down what actually became of each table and the API serves it back.
 *
 * Maps the recorded fate + the join failure to the sentence that is TRUE:
 *   finished            → «این بازی تمام شده است»
 *   closed              → «میزبان این میز را بست»
 *   playing + locked    → «بازی این میز شروع شده است»   (it really is in progress)
 *   waiting + locked    → «میز پر است»                   (locked while waiting = full)
 *   anything else       → the generic {@link joinErrorFa}
 *
 * Best-effort: if the lookup fails or we have no record, the generic wording stands. A
 * status lookup must never be what stops a player seeing why they could not join.
 */
export async function tableJoinReason(
  error: unknown,
  roomId: string,
  lookup: (roomId: string) => Promise<TableStatusResponse>,
): Promise<string | null> {
  const failure = roomFailure(error);
  if (failure === null) return null;
  const generic = failure === "locked" ? fa.invite.roomLocked : fa.invite.roomGone;
  try {
    const { state } = await lookup(roomId);
    if (state === "finished") return fa.invite.gameFinished;
    if (state === "closed") return fa.invite.tableClosed;
    if (state === "expired") return fa.invite.tableExpired;
    if (failure === "locked" && state === "playing") return fa.invite.gameStarted;
    if (failure === "locked" && state === "waiting") return fa.invite.roomFull;
    return generic;
  } catch {
    return generic;
  }
}

/**
 * WHAT BECAME OF A TABLE, IN ONE SENTENCE — for a recorded fate that shuts the door whatever the
 * refusal was (a «ادامه بازی» return to a table that is over; a rematch successor that no longer
 * takes players). Null for a table still filling or one with no record.
 */
export function tableFateLine(state: TableStatusResponse["state"]): string | null {
  if (state === "finished") return fa.invite.gameFinished;
  if (state === "closed") return fa.invite.tableClosed;
  if (state === "expired") return fa.invite.tableExpired;
  if (state === "playing") return fa.invite.gameStarted;
  return null;
}

/**
 * HOW A TABLE ENDED, as the status route reports it (contract C2, session B — 2026-10-08):
 * `end` is present only when the game-server recorded the terminal, `result` only for the asker's
 * own row. Read off the response by {@link readTableEnd}, never off a type: an OLD api sends no
 * `end` at all, and that must read as «nothing to say», never as a guess.
 */
export interface TableEnd {
  readonly reason: string;
  readonly result?: "win" | "loss" | "draw";
}

/** The `end` of a `GET /tables/:roomId/status` response, shape-checked — or null. */
export function readTableEnd(status: unknown): TableEnd | null {
  if (typeof status !== "object" || status === null) return null;
  const end = (status as { end?: unknown }).end;
  if (typeof end !== "object" || end === null) return null;
  const { reason, result } = end as { reason?: unknown; result?: unknown };
  if (typeof reason !== "string" || reason.length === 0) return null;
  return result === "win" || result === "loss" || result === "draw"
    ? { reason, result }
    : { reason };
}

/**
 * ONE LINE OF HOW, for the player coming BACK to a table that is over («این بازی تمام شده است» is
 * the line above it) — or null, and the first line stands alone.
 *
 * ⚠️ THE READER'S OWN RESULT AND FACTS ABOUT THE TABLE, NOTHING ELSE (`away-copy-rule.test.ts`):
 * «حریفت برد رو ادعا کرد» is what happened at the table, «باخت به نامت ثبت شد» is the reader's own
 * record; what the end did to anybody ELSE's record is never said. A reason this build does not
 * know says nothing rather than a guess.
 */
export function tableEndLine(end: TableEnd): string | null {
  const r = end.result;
  const plain =
    r === "win"
      ? fa.invite.endedWin
      : r === "loss"
        ? fa.invite.endedLoss
        : r === "draw"
          ? fa.invite.endedDraw
          : null;
  switch (end.reason) {
    case "claim":
      return r === "loss"
        ? fa.invite.endedClaimLoss
        : r === "draw"
          ? fa.invite.endedClaimDraw
          : plain;
    case "resign":
      return r === "loss" ? fa.invite.endedResignLoss : plain;
    case "timeout":
      return r === "loss"
        ? fa.invite.endedTimeoutLoss
        : r === "win"
          ? fa.invite.endedTimeoutWin
          : r === "draw"
            ? fa.invite.endedTimeoutDraw
            : null;
    case "idle":
      return r === "loss" ? fa.invite.endedIdleLoss : plain;
    case "end":
      return plain;
    default:
      return null;
  }
}

/** Persian (Arabic-block) letters. Our server-sent reasons (the coming-soon /
 *  max-games gates, invite failures) are Persian; a raw WebSocket / network /
 *  system string ("Error undefined", "WebSocket is closed") is pure ASCII. */
const HAS_PERSIAN = /[؀-ۿ]/;

/**
 * THE SERVER RETIRED THE SAVE THIS JOIN NAMED — made under rules the game no longer plays, deleted,
 * recorded as nothing (`SAVED_GAME_RETIRED_MESSAGE`). The ONE match for it, exact on the wire
 * string, shared by the notice ({@link localizedReason}) and the solo connector, which treats it as
 * terminal at both doors: the «ادامه بازی» join and the reconnect loop's re-join. Takes the join's
 * rejection (an Error) or the bare string a room's `onError` delivers.
 */
export function isSavedGameRetired(error: unknown): boolean {
  const message = typeof error === "string" ? error : error instanceof Error ? error.message : "";
  return message === SAVED_GAME_RETIRED_MESSAGE;
}

/**
 * A user-safe reason, or `null` to let the caller fall back to its OWN localized
 * default (e.g. the container's `error ?? fa.<game>.connectionError`). Keeps a
 * localized server message and DISCARDS any raw English transport/network string
 * — so a dropped socket or a failed join can never surface «Error undefined» or a
 * bare WebSocket error to the user. Pairs with the connectors NOT routing bare
 * transport errors at all (the lifecycle owns those) — this is defense in depth.
 */
export function localizedReason(error: unknown): string | null {
  const message = typeof error === "string" ? error : error instanceof Error ? error.message : "";
  // A SAVE MADE UNDER OLD RULES (the server deleted it and refused the «ادامه بازی» join): the exact
  // wire string becomes the app's own notice. Before the Persian pass-through, which would show the
  // contract string itself; every solo game reads its refusal through here (`createGameStore`).
  if (isSavedGameRetired(message)) return fa.status.savedGameRetired;
  if (HAS_PERSIAN.test(message)) return message;
  /**
   * ⚠️ A REFUSAL WE UNDERSTAND IS NOT A CONNECTION FAILURE, AND UNTIL NOW IT READ AS ONE.
   * Returning `null` here hands the container its `error ?? fa.<game>.connectionError` fallback —
   * «خطا در اتصال به سرور بازی» — which is a true sentence only when we genuinely could not reach
   * the server. Measured on prod over 24 h: **503 distinct players** got that line for a table
   * that was locked, gone, full or whose reservation had expired. `joinErrorFa` already holds the
   * right Persian sentence for the two room fates and returns `null` for anything it does not
   * recognise, so the generic fallback still covers real transport failures.
   */
  const seat = seatReservationExpired(message);
  if (seat) return seat;
  return joinErrorFa(error);
}

/**
 * THE TICKET DIED, NOT THE ROOM — a DIFFERENT thing from either room fate, and deliberately kept
 * out of {@link roomFailure}.
 *
 * `client.reconnect(token)` fails with «reconnection token invalid or expired.» when the
 * reservation behind it is spent; the table itself may be perfectly alive. Classifying it as a
 * room failure would make `isJoinFailure` true and `isRoomLocked` false, which changes what the
 * connectors DO — for 130 events / 110 players a day — to fix a label. So it only ever answers the
 * wording question, here.
 */
function seatReservationExpired(message: string): string | null {
  return /reconnection token invalid or expired/i.test(message) ? fa.invite.seatExpired : null;
}
